import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/InputDefault";
import { Textarea } from "@/components/ui/textarea";
import { Save, X } from "lucide-react";
import { useSimplifiedUserData } from "@/providers/SimplifiedUserDataProvider";
import { useToast } from "@/hooks/use-toast";
import { createTag } from "@/lib/stores/tagsStore";

import { usePlatformTranslation } from '@/hooks/usePlatformTranslation';
interface TagCreatorProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

interface GroupData {
  name: string;
  description: string;
  color: string;
  tags: string;
}

export default function TagCreator({ isOpen, onClose, onSuccess }: TagCreatorProps) {
  const { t } = usePlatformTranslation();
  const { selectedGame } = useSimplifiedUserData();
  const { toast } = useToast();

  // Color options for tag groups
  const tagColors = [
    "#6366f1", // Indigo
    "#ef4444", // Red
    "#10b981", // Green
    "#f59e0b", // Amber
    "#8b5cf6", // Violet
    "#06b6d4", // Cyan
    "#f97316", // Orange
    "#ec4899", // Pink
    "#84cc16", // Lime
    "#6b7280", // Gray
  ];

  const [groupData, setGroupData] = useState<GroupData>({
    name: "",
    description: "",
    color: "#6366f1",
    tags: "",
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async () => {
    if (!selectedGame || !groupData.name.trim() || !groupData.tags.trim()) {
      toast({
        title: "Error",
        description: "Please provide a group name and at least one tag",
        variant: "destructive",
      });
      return;
    }

    setIsSubmitting(true);
    
    try {
      // Parse comma-separated tags
      const tagNames = groupData.tags
        .split(",")
        .map(tag => tag.trim())
        .filter(tag => tag.length > 0);

      if (tagNames.length === 0) {
        toast({
          title: "Error",
          description: "Please provide at least one tag",
          variant: "destructive",
        });
        setIsSubmitting(false);
        return;
      }

      // Create each tag with the group information
      const promises = tagNames.map(tagName =>
        createTag({
          game_id: selectedGame.id,
          name: tagName,
          group: groupData.name.trim(),
          description: groupData.description.trim() || undefined,
          color: groupData.color,
        }, toast)
      );

      await Promise.all(promises);

      toast({
        title: "Success",
        description: `Created ${tagNames.length} tag${tagNames.length > 1 ? 's' : ''} in group "${groupData.name}"`,
      });

      // Reset form
      setGroupData({
        name: "",
        description: "",
        color: "#6366f1",
        tags: "",
      });

      onSuccess?.();
      onClose();
    } catch (error) {
      console.error("Error creating tags:", error);
      toast({
        title: "Error",
        description: "Failed to create tags. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    setGroupData({
      name: "",
      description: "",
      color: "#6366f1",
      tags: "",
    });
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="space-y-3 p-3 border border-input rounded">
      <div className="flex items-center justify-between">
        <span className="text-sm font-medium text-muted-foreground">{t('platform.cards.widgets.create_tag_group')}</span>
        <Button
          variant="ghost"
          size="sm"
          onClick={handleCancel}
          disabled={isSubmitting}
          className="h-6 w-6 p-0"
        >
          <X className="h-3 w-3" />
        </Button>
      </div>

      <div className="space-y-3">
        {/* Group Name */}
        <div>
            <label className="block text-xs font-medium text-muted-foreground mb-1">
            Group Name *
          </label>
          <Input
            value={groupData.name}
            onChange={(e) => setGroupData({ ...groupData, name: e.target.value })}
            placeholder={t('platform.cards.widgets.eg_weapons_characters_ui_elements')}
            disabled={isSubmitting}
            size={8}
          />
        </div>

        {/* Group Color */}
        <div>
          <label className="block text-xs font-medium text-muted-foreground mb-1">
            Group Color *
          </label>
          <div className="flex items-center gap-2">
            {/* Color Picker */}
            <input
              type="color"
              value={groupData.color}
              onChange={(e) => setGroupData({ ...groupData, color: e.target.value })}
              disabled={isSubmitting}
              className="w-8 h-8 rounded border border-input cursor-pointer"
            />
            {/* Hash Code Input */}
            <Input
              value={groupData.color}
              onChange={(e) => setGroupData({ ...groupData, color: e.target.value })}
              placeholder="#6366f1"
              disabled={isSubmitting}
              size={8}
              className="w-24"
            />
            {/* Quick Color Presets */}
            <div className="flex gap-1">
              {tagColors.slice(0, 5).map((color) => (
                <button
                  key={color}
                  type="button"
                  className={`w-5 h-5 rounded border hover:scale-110 transition-transform ${
                    groupData.color === color ? 'border-foreground border-2' : 'border-input'
                  }`}
                  style={{ backgroundColor: color }}
                  onClick={() => setGroupData({ ...groupData, color })}
                  disabled={isSubmitting}
                  title={color}
                />
              ))}
            </div>
          </div>
        </div>

        {/* Tags (Comma Separated) */}
        <div>
          <label className="block text-xs font-medium text-muted-foreground mb-1">
            Tags *
          </label>
          <Input
            value={groupData.tags}
            onChange={(e) => setGroupData({ ...groupData, tags: e.target.value })}
            placeholder={t('platform.cards.widgets.sword_shield_armor_helmet')}
            disabled={isSubmitting}
            size={8}
          />
          <p className="text-xs text-muted-foreground mt-1">
            Enter tags separated by commas
          </p>
        </div>

        {/* Group Description */}
        <div>
          <label className="block text-xs font-medium text-muted-foreground mb-1">
            Description
          </label>
          <Textarea
            value={groupData.description}
            onChange={(e) => setGroupData({ ...groupData, description: e.target.value })}
            placeholder={t('platform.cards.widgets.optional_description')}
            rows={2}
            disabled={isSubmitting}
          />
        </div>

        {/* Preview */}
        {groupData.tags.trim() && (
          <div>
            <label className="block text-xs font-medium text-muted-foreground mb-1">
              Preview
            </label>
            <div className="flex flex-wrap gap-1.5 p-2 bg-muted rounded border">
              {groupData.tags
                .split(",")
                .map(tag => tag.trim())
                .filter(tag => tag.length > 0)
                .map((tag, index) => (
                  <span
                    key={index}
                      className="inline-flex items-center gap-1.5 px-2 py-1 rounded-full text-xs font-medium text-foreground"
                    style={{ backgroundColor: groupData.color }}
                  >
                    <div className="w-1.5 h-1.5 rounded-full bg-foreground bg-opacity-40" />
                    {tag}
                  </span>
                ))}
            </div>
          </div>
        )}
      </div>

      <div className="flex gap-2">
        <Button 
          onClick={handleSubmit} 
          size="sm" 
          className="flex items-center gap-1"
          disabled={isSubmitting || !groupData.name.trim() || !groupData.tags.trim()}
        >
          <Save className="h-3 w-3" />
          {isSubmitting ? "Creating..." : "Create"}
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={handleCancel}
          disabled={isSubmitting}
        >
          Cancel
        </Button>
      </div>
    </div>
  );
}
